/**
 * Azerbaijani formatting for the panel.
 *
 * Relative times and labels that the *app* shows come from the server, so the
 * three clients cannot drift. What lives here is panel-only chrome: absolute
 * dates in tables, grouped numbers, and delta badges.
 */

const AZ = 'az-AZ'
const BAKU = 'Asia/Baku'

/**
 * Abbreviated Azerbaijani month names.
 *
 * Intl's az-AZ short month gives "M09" rather than "sen", which is what the
 * design prints ("1 avq – 3 sen 2026"), so the table is spelled out here.
 */
const MONTHS_SHORT = [
  'yan', 'fev', 'mar', 'apr', 'may', 'iyn',
  'iyl', 'avq', 'sen', 'okt', 'noy', 'dek',
] as const

/** Day and month in Baku time, e.g. "3 sen". */
function dayAndMonth(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'numeric',
    timeZone: BAKU,
  }).formatToParts(at)

  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const monthIndex = Number(parts.find((p) => p.type === 'month')?.value ?? '1') - 1
  return `${day} ${MONTHS_SHORT[monthIndex] ?? ''}`
}

/** Four-digit year in Baku time. */
function yearOf(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', timeZone: BAKU }).format(at)
}

/**
 * Thousands separated the way the design prints them: "12 480", "612 430".
 *
 * Grouped from the digits rather than left to Intl: this runtime's az-AZ locale
 * groups with a comma, and the separator varies with the ICU data a browser
 * ships, so relying on it made the panel disagree with the design.
 */
export function number(value: number): string {
  if (!Number.isFinite(value)) return '—'

  const negative = value < 0
  const [whole = '', fraction] = Math.abs(value).toString().split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  // A true minus sign, matching the delta badges.
  return `${negative ? '−' : ''}${grouped}${fraction ? `,${fraction}` : ''}`
}

export function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${value}%`
}

/** "3 sen 2026" — the format used in the moderators table. */
export function shortDate(iso: string): string {
  const at = new Date(iso)
  return `${dayAndMonth(at)} ${yearOf(at)}`
}

/** "bugün 09:32", "dünən 21:12", else "3 sen 09:32" — the journal's VAXT column. */
export function journalTime(iso: string): string {
  const at = new Date(iso)
  const time = new Intl.DateTimeFormat(AZ, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BAKU,
  }).format(at)

  const dayOf = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: BAKU }).format(d)

  const now = new Date()
  const yesterday = new Date(now.getTime() - 86_400_000)

  if (dayOf(at) === dayOf(now)) return `bugün ${time}`
  if (dayOf(at) === dayOf(yesterday)) return `dünən ${time}`

  return `${dayAndMonth(at)} ${time}`
}

/** Chart axis label: "1 avq", "20 avq". */
export function axisDate(isoDay: string): string {
  return dayAndMonth(new Date(`${isoDay}T00:00:00Z`))
}

/** "+18%" / "−4%" — a true minus sign, as the design uses. */
export function delta(changePct: number | null | undefined): string | null {
  if (changePct === null || changePct === undefined) return null
  if (changePct === 0) return '0%'
  return changePct > 0 ? `+${changePct}%` : `−${Math.abs(changePct)}%`
}

export function deltaTone(changePct: number | null | undefined): 'up' | 'down' | 'flat' {
  if (changePct === null || changePct === undefined || changePct === 0) return 'flat'
  return changePct > 0 ? 'up' : 'down'
}

/** Queue health line on screen 7a: "orta cavab vaxtı 3s 40d". */
export function durationFromMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—'
  const whole = Math.round(minutes)
  if (whole < 60) return `${whole}d`
  const hours = Math.floor(whole / 60)
  const rest = whole % 60
  return rest === 0 ? `${hours}s` : `${hours}s ${rest}d`
}

/** Maps a report priority to its chip class. */
export function priorityClass(priority: 'URGENT' | 'MEDIUM' | 'LOW'): string {
  switch (priority) {
    case 'URGENT':
      return 'chip chip--urgent'
    case 'MEDIUM':
      return 'chip chip--medium'
    case 'LOW':
      return 'chip chip--low'
  }
}
